import React from 'react';
import PropTypes from 'prop-types';
import { connectToJSContext, RA_73_STERILIZE_HTML_STRING } from '../utils.jsx';
import { Utils, ImageResizing, ViewHelpers } from 'poly-js-utils';
import { makeDesignationString, makeStateLicensesString, checkEmptyState } from '../helpers.jsx';
import TeamMember from './team-member.jsx';
import SectionHeader from '../section_header/index.jsx';
import ScrollToLink from '../scroll_to_link/index.jsx';
import { mapTeamMembersToOffices } from './selectors';
import { TEAM_MEMBERS, ANIMATION_SCROLL_TIME, TEAM_MEMBER } from '../constants';

const getVisibleTextLength = (htmlString) => {
    const visibleTextContainer = document.createElement('div');
    visibleTextContainer.innerHTML = htmlString;

    const text = visibleTextContainer.innerText || visibleTextContainer.textContent;
    return text.length;
};

const OfficeAnchors = ({offices, officesWithTeams}) => (
    <div className="office-anchor">
        {/*
            officeList.filter(Boolean).length > 1 returns the amount of populated offices.
            This keeps the page from rendering an anchor for a single office.
        */}
        {officesWithTeams && officesWithTeams.filter(Boolean).length > 1 &&
            officesWithTeams.map((office, i) => offices[i] &&
            <ScrollToLink
                animationTime={ANIMATION_SCROLL_TIME}
                aria-controls={`office_${i}`}
                aria-label={offices[i]}
                className={`team-members-office-link_${i}`}
                data-toggle="tab"
                href={`#office_${i}`}
                key={i}
                role="tab"
            >
                {offices[i]}
            </ScrollToLink>
            )}
    </div>
);

const TeamMembers = ({
    teamMembers,
    emptyTeamMembersText,
    headerText,
    offices,
    teamMemberLabels,
    hideOfficeNames
}) => {
    const hasTeamMembers = Boolean(teamMembers && teamMembers.length);
    const officesWithTeams = mapTeamMembersToOffices(offices, teamMembers);
    const maxVerticalBioLength = 750;
    const isHorizontalBioForced = !!officesWithTeams.map(
        team => !!team.find(
            member => getVisibleTextLength(member.bio) > maxVerticalBioLength
        )
    ).find(isBioToolong => !!isBioToolong);

    return (
        (hasTeamMembers || emptyTeamMembersText) ?
            <div className="hs-team-members">
                <SectionHeader
                    title={headerText}
                />
                <div className="c-team-members">
                    <div className="container">
                        {!hideOfficeNames &&
                            <OfficeAnchors
                                offices={offices}
                                officesWithTeams={officesWithTeams}
                                hideOfficeNames={hideOfficeNames}
                            />
                        }
                        {hasTeamMembers ? officesWithTeams.map((members, i) => (
                            <div className="row c-team-members__section" key={i}>
                                {!hideOfficeNames && offices[i] &&
                                    <h2 id={`office_${i}`}>{offices[i]}</h2>
                                }
                                {officesWithTeams[i].map((member, j) => (
                                    <TeamMember
                                        {...member}
                                        teamMemberLabels={teamMemberLabels}
                                        key={j}
                                        memberId={j}
                                        isHorizontalBioForced={isHorizontalBioForced}
                                    />
                                ))}
                            </div>
                        )) :
                            <div className="row c-no-team">
                                <div className="col-xs-12"
                                    dangerouslySetInnerHTML={{
                                        __html: RA_73_STERILIZE_HTML_STRING(emptyTeamMembersText)
                                    }}
                                />
                            </div>
                        }
                    </div>
                </div>
            </div>
            : null
    );
};

TeamMembers.propTypes = {
    teamMembers: PropTypes.array
};

const mapStateToProps = state => ({
    teamMembers: state.profile.team_members && state.profile.team_members.map((member) => {
        return {
            name: `${member.team_member_first_name || ''} ${member.team_member_last_name || ''}`,
            location: member.team_member_location,
            associatedOffice: (member.associated_office && member.associated_office[0]) ||
                TEAM_MEMBERS.ASSOCIATED_OFFICE,
            bio: member.team_member_bio,
            email: member.team_member_email,
            phone: ViewHelpers.phoneFormat(member.team_member_phone, true),
            phoneFormatted: ViewHelpers.phoneFormat(member.team_member_phone, false),
            fax: ViewHelpers.phoneFormat(member.team_member_fax, true),
            photo: member.team_member_photo ?
                ImageResizing.resize(member, 'team_member_photo', 255) :
                ImageResizing.resize(state.theme_data.about_page, 'default_image', 255),
            url: member.team_member_url,
            photoResizeReady: member.team_member_photo_resize_ready,
            title: member.team_member_title,
            designationString: (member.designations || member.additional_designations) &&
                makeDesignationString(
                    member.designations,
                    state.categories.designations,
                    member.additional_designations
                ),
            licenses: member.team_member_licenses &&
                makeStateLicensesString(member.team_member_licenses, member),
            social: [
                {
                    type: 'facebook',
                    url: member.team_member_facebook
                },
                {
                    type: 'twitter',
                    url: member.team_member_twitter
                },
                {
                    type: 'linkedin',
                    url: member.team_member_linkedin
                },
                {
                    type: 'instagram',
                    url: member.team_member_instagram
                }
            ],
            externalWindowLinkAriaText: state.theme_data.labels.externalWindowLinkAriaText
        };
    }),
    offices: (state.profile.office_title && state.profile.suboffices && [
        state.profile.office_title,
        ...state.profile.suboffices.map((office) => office.office_title || office.street)
    ]) || [],
    emptyTeamMembersText: checkEmptyState(state.theme_data.about_page.empty_state_text),
    headerText: state.theme_data.about_page.team_header_title || TEAM_MEMBERS.HEADER_TEXT,
    teamMemberLabels: {
        job_title: state.theme_data.about_page.job_title || TEAM_MEMBERS.LABELS.JOB_TITLE,
        state_licenses: state.theme_data.about_page.state_licenses || TEAM_MEMBERS.LABELS.STATE_LICENSES,
        location: state.theme_data.about_page.location || TEAM_MEMBERS.LABELS.LOCATION,
        website: state.theme_data.about_page.website || TEAM_MEMBERS.LABELS.WEBSITE,
        email: state.theme_data.about_page.email || TEAM_MEMBERS.LABELS.EMAIL,
        phone: state.theme_data.about_page.phone || TEAM_MEMBERS.LABELS.PHONE,
        fax: state.theme_data.about_page.fax || TEAM_MEMBERS.LABELS.FAX,
        callMeLabel: state.theme_data.labels.team_member_call_me || TEAM_MEMBER.CALL_ME_TEXT,
        emailMeLabel: state.theme_data.labels.team_member_email_me || TEAM_MEMBER.EMAIL_ME_TEXT
    },
    hideOfficeNames: Utils.selectBoxIsTrue(state.profile.hide_office_names)
});

export default connectToJSContext(mapStateToProps)(TeamMembers);



// WEBPACK FOOTER //
// ./components/team_members/index.jsx